import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { AmSowLibraryTask } from '../../lib/types';
import { SOW_AREAS } from './AmSow';

/**
 * The scope task library.
 *
 * The list every scope of work is built out of, grouped by the work area it
 * belongs under. Before it existed, the same fifteen lines were typed into
 * every firewall swap and every office move this company scoped, which is how
 * two scopes for the same job end up quoting different hours for "configure
 * the VPN".
 *
 * MOST OF IT IS NOT MAINTAINED HERE. The star on a task row in the builder is
 * what actually fills this up — somebody writes a good task on a real scope
 * and keeps it — and this screen is for the tidying afterwards: fixing the
 * wording, correcting a default, throwing out the one that was a typo. A
 * library whose only entry point was a blank form on a separate tab would
 * stay as empty as the day it shipped.
 *
 * Deleting is safe and unguarded, unlike most library screens in this app. A
 * scope COPIES the wording in rather than pointing at it, so nothing in the
 * database references these rows and removing one cannot change a document
 * that has already gone to a client.
 */
export function AmSowTasks() {
  const resource = useResource<AmSowLibraryTask[]>('/am/sow-tasks');
  const [area, setArea] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AmSowLibraryTask | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /* A SEARCH TURNS DRAGGING OFF, and this is the one rule worth stating.
     Reordering writes a position for every task in the area; a search shows a
     SUBSET, so renumbering what is on screen from zero would interleave those
     rows with the ones the search hid. Filtering by area is fine — that group
     is still the whole area — so only the text box disables it. */
  const searching = search.trim() !== '';

  const tasks = useMemo(() => resource.data ?? [], [resource.data]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const shown = tasks.filter((t) => {
      if (area && t.AreaKey !== area) return false;
      if (!q) return true;
      return [t.Name, t.Description].filter(Boolean).some((v) => v!.toLowerCase().includes(q));
    });
    /* Grouped in the builder's own area order rather than alphabetically, so
       the library reads down the page in the order a scope is written. Any
       area key the builder does not know about still gets a group, under its
       raw key — see the note on AreaKey in migration 124. */
    const known = SOW_AREAS.map((a) => a.key) as string[];
    const keys = [...new Set(shown.map((t) => t.AreaKey))].sort(
      (a, b) =>
        (known.indexOf(a) === -1 ? 99 : known.indexOf(a)) -
        (known.indexOf(b) === -1 ? 99 : known.indexOf(b)),
    );
    return keys.map((key) => ({
      key,
      name: SOW_AREAS.find((a) => a.key === key)?.name ?? key,
      tasks: shown.filter((t) => t.AreaKey === key),
    }));
  }, [tasks, area, search]);

  const shownCount = grouped.reduce((n, g) => n + g.tasks.length, 0);

  if (resource.loading) return <Loading label="Loading the task library" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  /**
   * Moves a task within its work area and writes the whole area's new order.
   *
   * Optimistic, so the row lands where it was dropped rather than after a round
   * trip — then every position in that area is written in one request. Put back
   * on failure: a row that stays where it was dropped but is somewhere else on
   * the next reload is worse than one that visibly refuses to move.
   */
  async function reorder(areaKey: string, e: DragEndEvent) {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;

    const group = tasks.filter((t) => t.AreaKey === areaKey);
    const from = group.findIndex((t) => t.AmSowTaskId === activeId);
    const to = group.findIndex((t) => t.AmSowTaskId === overId);
    if (from < 0 || to < 0) return;
    const moved = arrayMove(group, from, to);

    /* Rebuilt in place: the k-th row of this area becomes the k-th of the moved
       list, so the positions the area occupies in the full list are untouched
       and no other area shifts. */
    let i = 0;
    const next = tasks.map((t) => (t.AreaKey === areaKey ? (moved[i++] as AmSowLibraryTask) : t));
    resource.set(() => next);
    setError(null);
    try {
      await api.put('/am/sow-tasks/order', { taskIds: moved.map((t) => t.AmSowTaskId) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the new order');
      resource.reload();
    }
  }

  async function remove(task: AmSowLibraryTask) {
    try {
      await api.del(`/am/sow-tasks/${task.AmSowTaskId}`);
      resource.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the task');
    }
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <div className="row">
          <select
            className="select"
            style={{ maxWidth: 240 }}
            value={area}
            onChange={(e) => setArea(e.target.value)}
            aria-label="Filter tasks by work area"
          >
            <option value="">Every work area</option>
            {SOW_AREAS.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
          <input
            className="input"
            style={{ maxWidth: 260 }}
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search the task library"
          />
          <span className="xs muted">
            {shownCount} of {tasks.length}
          </span>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          Add a task
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {tasks.length === 0 ? (
        <Empty title="The library is empty">
          <p>
            Add tasks here, or build them on a scope of work and press ☆ on the row to keep one.
            Picked tasks land on a scope as ordinary editable rows — the hours and wording are
            adjusted per engagement, which is the part that actually varies.
          </p>
        </Empty>
      ) : shownCount === 0 ? (
        <Empty title="Nothing matches that filter" />
      ) : (
        <div className="stack">
          {grouped.map((group) => (
            <section className="card" key={group.key}>
              <div className="card-head">
                <div className="grow">
                  <span className="eyebrow">Work area</span>
                  <h2>{group.name}</h2>
                </div>
                <span className="xs muted">
                  {group.tasks.length} task{group.tasks.length === 1 ? '' : 's'}
                </span>
              </div>
              {/* One context per area: the areas are separate tables and a task
                  never moves between them here — that is what the work-area
                  dropdown on the edit form is for.

                  closestCorners, not closestCenter - see the note in
                  AmSow.tsx. With closestCenter every arrow press during a
                  keyboard drag resolved back to the row being dragged, so
                  the lift worked and the row could never move. */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={(e) => void reorder(group.key, e)}
              >
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      {!searching && <th style={{ width: 24 }} aria-label="Reorder" />}
                      <th style={{ width: '30%' }}>Task</th>
                      <th>Description</th>
                      <th className="num" style={{ width: 110 }}>Usual hours</th>
                      <th style={{ width: 150 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {/* A context provider renders no DOM, so it sits inside
                        <tbody> without putting anything between it and its
                        rows — which a <div> here would, and the browser would
                        hoist it straight back out of the table. */}
                    <SortableContext
                      items={group.tasks.map((t) => t.AmSowTaskId)}
                      strategy={verticalListSortingStrategy}
                    >
                    {group.tasks.map((task) => (
                      <SortableLibraryRow
                        key={task.AmSowTaskId}
                        task={task}
                        /* One row has nothing to reorder, and a search is
                           showing a subset — see `searching` above. */
                        canReorder={!searching && group.tasks.length > 1}
                        onEdit={() => setEditing(task)}
                        onRemove={() => remove(task)}
                      />
                    ))}
                    </SortableContext>
                  </tbody>
                </table>
              </div>
              </DndContext>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <TaskEditor
          task={editing === 'new' ? null : editing}
          defaultArea={area || SOW_AREAS[0]?.key || 'project_management'}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            resource.reload();
          }}
        />
      )}
    </>
  );
}

function TaskEditor({
  task,
  defaultArea,
  onClose,
  onSaved,
}: {
  task: AmSowLibraryTask | null;
  defaultArea: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [areaKey, setAreaKey] = useState(task?.AreaKey ?? defaultArea);
  const [name, setName] = useState(task?.Name ?? '');
  const [description, setDescription] = useState(task?.Description ?? '');
  const [hours, setHours] = useState(task?.DefaultHours != null ? String(task.DefaultHours) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        areaKey,
        name: name.trim(),
        description: description.trim() || null,
        /* An empty box is no usual size, not zero hours. A default of 0 would
           put a figure on every scope that picks it and make somebody delete
           it every time. */
        defaultHours: hours.trim() === '' ? null : Number.parseFloat(hours) || 0,
      };
      if (task) await api.put(`/am/sow-tasks/${task.AmSowTaskId}`, body);
      else await api.post('/am/sow-tasks', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the task');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={task ? 'Edit the task' : 'Add a task to the library'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="Work area" hint="Which step of the builder offers it.">
        <select className="select" value={areaKey} onChange={(e) => setAreaKey(e.target.value)}>
          {SOW_AREAS.map((a) => (
            <option key={a.key} value={a.key}>
              {a.name}
            </option>
          ))}
          {/* An area key the builder no longer knows about stays selectable so
              editing such a row does not silently move it. */}
          {!SOW_AREAS.some((a) => a.key === areaKey) && (
            <option value={areaKey}>{areaKey}</option>
          )}
        </select>
      </Field>
      <Field label="Task">
        <input
          className="input"
          autoFocus
          placeholder="e.g. Configure network and firewall rules"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Description" hint="Lands on the scope as written. Say what is actually done.">
        <textarea
          className="textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field
        label="Usual hours"
        hint="Optional. Offered as the starting figure when the task is picked, and edited per engagement."
      >
        <input
          className="input"
          type="number"
          min="0"
          step="0.25"
          placeholder="e.g. 6"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
      </Field>
      {error && <ErrorBanner message={error} />}
    </Modal>
  );
}


/* =========================================================================
   One library row
   ========================================================================= */

/**
 * A library task, draggable by its grip.
 *
 * The same ⠿ and the same rules as the builder's task rows — only the grip
 * starts a drag, and it fades in on hover or focus — but this one is a table
 * row, so the transform goes on the <tr> and the grip gets its own cell.
 */
function SortableLibraryRow({
  task,
  canReorder,
  onEdit,
  onRemove,
}: {
  task: AmSowLibraryTask;
  canReorder: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.AmSowTaskId, disabled: !canReorder });

  return (
    <tr
      ref={setNodeRef}
      className={isDragging ? 'dragging' : undefined}
      style={
        {
          transform: CSS.Transform.toString(transform),
          transition,
          /* Lifted above its neighbours so it passes over them rather than
             through them. A transformed <tr> does not create a stacking
             context on its own, hence position as well. */
          ...(isDragging ? { position: 'relative', zIndex: 2, opacity: 0.65 } : null),
        } as CSSProperties
      }
    >
      {canReorder && (
        <td className="am-task-drag-cell">
          <button
            type="button"
            className="am-task-drag"
            ref={setActivatorNodeRef}
            title="Drag to reorder"
            aria-label={`Reorder ${task.Name}`}
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        </td>
      )}
      <td className="strong">{task.Name}</td>
      <td className="xs muted">{task.Description || '—'}</td>
      <td className="num">{task.DefaultHours != null ? task.DefaultHours.toFixed(1) : '—'}</td>
      <td className="actions">
        <span className="row">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
            Edit
          </button>
          <DeleteButton
            what={`"${task.Name}"`}
            consequence="Scopes already written keep their copy of it."
            onConfirm={onRemove}
          />
        </span>
      </td>
    </tr>
  );
}
